import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { BookOpen, MapPin, Calendar, ArrowLeft, Loader2, Smile, Heart, Star, Sun, Meh, Frown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MOODS = {
  happy: { icon: Smile, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  love: { icon: Heart, color: 'text-rose-400', bg: 'bg-rose-500/10' },
  excited: { icon: Star, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  relaxed: { icon: Sun, color: 'text-sky-400', bg: 'bg-sky-500/10' },
  neutral: { icon: Meh, color: 'text-slate-400', bg: 'bg-slate-500/10' },
  tired: { icon: Frown, color: 'text-orange-400', bg: 'bg-orange-500/10' },
};

export default function PublicDiary({ shareId, onBack, onNavigate }) {
  const { i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!shareId) return;
    fetch(`${API}/diary/public/${shareId}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl text-white font-semibold mb-2">Diario non trovato</h2>
          <p className="text-slate-400 text-sm mb-6">Questo link potrebbe essere scaduto o non valido.</p>
          <button onClick={onBack} className="text-teal-400 hover:text-teal-300 text-sm flex items-center gap-1 mx-auto">
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16" data-testid="public-diary">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-teal-400 mb-3 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20">
            <BookOpen className="w-3.5 h-3.5" /> Travel Diary
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-2">
            {data.traveler_name}
          </h1>
          <p className="text-slate-400 text-sm">{data.entries.length} {data.entries.length === 1 ? 'entry' : 'entries'}</p>
        </motion.div>

        {/* Entries */}
        <div className="space-y-6">
          {data.entries.map((entry, i) => {
            const mood = MOODS[entry.mood] || MOODS.happy;
            const MoodIcon = mood.icon;
            return (
              <motion.div key={entry.entry_id}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/[0.04] border border-white/[0.08] rounded-2xl overflow-hidden"
                data-testid={`public-entry-${entry.entry_id}`}>
                
                {entry.photo_url && (
                  <div className="h-52 overflow-hidden">
                    <img src={entry.photo_url} alt={entry.title} className="w-full h-full object-cover" onError={e => e.target.style.display = 'none'} />
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h2 className="text-xl text-white font-semibold font-outfit">{entry.title}</h2>
                      <div className="flex items-center gap-4 mt-1.5">
                        <span className="flex items-center gap-1 text-xs text-teal-400">
                          <MapPin className="w-3 h-3" /> {entry.destination}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <Calendar className="w-3 h-3" /> {new Date(entry.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className={`w-9 h-9 rounded-full ${mood.bg} flex items-center justify-center`}>
                      <MoodIcon className={`w-4.5 h-4.5 ${mood.color}`} />
                    </div>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">{entry.content}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="text-center mt-12 pt-8 border-t border-white/[0.06]">
          <p className="text-slate-500 text-sm mb-3">Powered by eSIM4You</p>
          <button onClick={() => onNavigate('shop')}
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-black font-bold px-6 py-2.5 rounded-full text-sm transition-all"
            data-testid="public-diary-cta">
            Scopri le nostre eSIM
          </button>
        </motion.div>
      </div>
    </div>
  );
}
